import {useState} from "react"
import {
    Table,
    TableBody,
    TableCell,
    TableRow,
    TableHead,
    TableContainer,
    Typography,
    Checkbox,
    Box,
    Tooltip, IconButton, Paper, Chip,
} from "@mui/material"
import dayjs from "dayjs"
import {useTranslation} from "react-i18next"
import {OpenInNew} from "@mui/icons-material"
import QuantitySelector from "./quantity-selector"
import {useBasket} from "../providers/basket-context"
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import AppliedDiscount from "./common/applied-discount";
import {cartLineTotal} from "./common/cart-pricing";

const TableContainerComp = ({
                                rows,
                                isDistributor,
                                isCart,
                                handleSelectAll,
                                handleRowSelect,
                                isAllSelected,
                                selectedRows,
                                onProductNameClick,
                            }) => {
    const {t} = useTranslation()
    const [productCounts, setProductCounts] = useState(() => {
        // Initialize with existing cart quantities
        const initialCounts = {};
        rows.forEach((row, index) => {
            if (row.cartQuantity) {
                initialCounts[index] = row.cartQuantity;
            }
        });
        return initialCounts;
    })
    const {addToBasket, deleteBasketItem, refreshBasketData} = useBasket()
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    })

    const handleCloseSnackbar = () => {
        setSnackbar({
            ...snackbar,
            open: false,
        })
    }

    const handleRemoveItem = async (cartId) => {
        try {
            console.log(cartId)
            await deleteBasketItem(cartId)
            if (refreshBasketData) {
                refreshBasketData()
            }
        } catch (error) {
            console.error("Error removing item:", error)
        }
    }

    const handleInputChange = async (index, newValue) => {
        try {
            const prevValue = productCounts[index] || 0
            const numValue = Number(newValue)

            console.log("handleInputChange - index:", index, "newValue:", newValue, "prevValue:", prevValue)

            // Update local state immediately for responsive UI
            setProductCounts((prevCounts) => ({
                ...prevCounts,
                [index]: numValue,
            }))

            // Only call backend if:
            // 1. Value actually changed
            // 2. Value is not zero (assuming you don't want to add 0 quantity)
            if (numValue !== prevValue && numValue > 0) {
                // Get the row data for this index
                const row = rows[index]

                if (row) {
                    await addToBasket({
                        productTenantId: row.productTenantId || row.id,
                        count: numValue,
                    })
                }
            }
        } catch (error) {
            console.error("Error updating basket:", error)
        }
    }

    const handleAddToBasketTrigger = async (index, row) => {
        try {
            const quantity = productCounts[index] || 0
            if (quantity > 0 && quantity <= row.quantity) {
                await addToBasket({
                    productTenantId: row.productTenantId || row.id,
                    count: quantity,
                })
            }
            if (refreshBasketData) {
                refreshBasketData()
            }
        } catch (error) {
            console.error("Error adding to basket:", error)
        }
    }

    const calculateItemTotal = (row, index) => {
        // Use local state quantity if available, otherwise use row's cartQuantity
        const quantity = productCounts[index] !== undefined ? productCounts[index] : (row.cartQuantity || 0);
        return cartLineTotal(row, quantity).toFixed(2);
    }

    return (
        <>
            <TableContainer component={Paper} sx={{mb: 3}}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {isCart && (
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={selectedRows?.length > 0 && selectedRows?.length < rows.length}
                                        checked={isAllSelected}
                                        onChange={handleSelectAll}
                                    />
                                </TableCell>
                            )}
                            <TableCell>{t("ProdCode")}</TableCell>
                            <TableCell>{t("ProdName")}</TableCell>
                            {isDistributor && (<TableCell>{t("Distributor")}</TableCell>)}
                            <TableCell>{t("Country")}</TableCell>
                            <TableCell>{t("Manufacturer")}</TableCell>
                            <TableCell>{t("Category")}</TableCell>
                            <TableCell>{t("ExpirationDate")}</TableCell>
                            <TableCell>{t("Price")}</TableCell>
                            {isCart && <TableCell align="right">{t("Total")}</TableCell>}
                            <TableCell align="center">{t("Availability")}</TableCell>
                            <TableCell align="center">{!isCart && t("InBasket")}</TableCell>

                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{py: 4}}>
                                    <Typography variant="body1" color="text.secondary">
                                        {t("ProductNotFound")}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (rows.map((row, index) => (
                            <TableRow
                                key={row.id || index} hover>
                                {isCart && (
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedRows?.includes(row.cartId)}
                                            onChange={() => handleRowSelect && handleRowSelect(row.cartId)}
                                        />
                                    </TableCell>
                                )}
                                <TableCell>
                                    <Typography variant="body2" fontWeight="medium">
                                        {row.productCode}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Tooltip title={row.productName} placement="top-start" arrow enterDelay={500}
                                             leaveDelay={200}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                cursor: "pointer",
                                                "&:hover": {color: "primary.main"},
                                            }}
                                            onClick={() => onProductNameClick && onProductNameClick(row)}
                                        >
                                            <Typography
                                                variant="body2"
                                                noWrap
                                                sx={{
                                                    maxWidth: {xs: "100px", sm: "250px", md: "200px"},
                                                    mr: 1,
                                                }}
                                            >
                                                {row.productName}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                </TableCell>
                                {isDistributor && (
                                    <TableCell>
                                        <Box sx={{display: "flex", alignItems: "center", justifyContent: 2}}>
                                            <Typography variant="body2">{row.tenantName}</Typography>
                                            <IconButton
                                                size="small"
                                                onClick={() => window.open(`/distributors/distributor/${row.tenantId}`)}
                                                sx={{ml: 1}}
                                            >
                                                <OpenInNew sx={{width: 17, height: 17}}/>
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                )}

                                <TableCell>
                                    <Typography variant="body2">{row.productCountry}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{row.productManufacturer}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip label={t(row.productCategory)} size="small" variant="outlined"
                                          color="primary"/>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="body2">{row.endDate === null ? "**/****" : dayjs(row.endDate.toString()).format("MM/YYYY")}</Typography>
                                </TableCell>
                                <TableCell>
                                    <AppliedDiscount product={row}/>
                                </TableCell>


                                {isCart && (
                                    <TableCell align="right">
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: "bold",
                                                color: "primary.main"
                                            }}
                                        >
                                            {calculateItemTotal(row, index)}֏
                                        </Typography>
                                    </TableCell>
                                )}

                                {!isCart && (
                                    <TableCell align="center">
                                        <Typography
                                            variant="body2"
                                            color={row.inStock ? "green" : "red"}
                                            fontWeight="bold"
                                        >
                                            {row.inStock ? t("InStock") : t("OutOfStock")}
                                        </Typography>
                                    </TableCell>
                                )}

                                <TableCell>
                                    <div style={{display: "flex", alignItems: "center", justifyContent: "center"}}>
                                        <QuantitySelector
                                            initialValue={row.cartQuantity || 0}
                                            min={1}
                                            max={isCart ? 999 : row.quantity}
                                            onChange={(newValue) => handleInputChange(index, newValue)}
                                            onBlur={() => handleAddToBasketTrigger(index, row)}
                                            onAddToBasket={() => handleAddToBasketTrigger(index, row)}
                                            index={index}
                                            row={row}
                                        />
                                    </div>
                                </TableCell>

                                {isCart && (<TableCell padding="checkbox" sx={{width: "40px"}}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemoveItem(row.cartId)}
                                            aria-label={`Remove ${row.productName}`}
                                        >
                                            <ClearOutlinedIcon fontSize="small"/>
                                        </IconButton>
                                    </TableCell>
                                )}
                            </TableRow>
                        )))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )
}

export default TableContainerComp
